import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Check, Package, Loader2, AlertCircle, ChevronLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-NZ", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-NZ", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function OrderStatus() {
  const [email, setEmail] = useState("");
  const [ordersList, setOrdersList] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  const search = async (e) => {
    e?.preventDefault?.();
    const q = email.trim().toLowerCase();
    if (!q) return;
    setLoading(true);
    setError("");
    setOrder(null);
    setOrdersList(null);
    try {
      const { data } = await axios.get(
        `${API}/orders/track-by-email/${encodeURIComponent(q)}`,
      );
      setOrdersList(data);
      // Auto-select if exactly one order for this email
      if (data.length === 1) {
        await openOrder(data[0].invoice_number);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "We couldn't find any orders for that email. Please check and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const openOrder = async (invoice) => {
    setDetailLoading(true);
    setError("");
    try {
      const { data } = await axios.get(
        `${API}/orders/track/${encodeURIComponent(invoice)}`,
      );
      setOrder(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not load that order.");
    } finally {
      setDetailLoading(false);
    }
  };

  const backToList = () => setOrder(null);

  return (
    <div
      data-testid="order-status-page"
      className="relative min-h-screen bg-[#0a0a0c] pt-32 lg:pt-40"
    >
      <section className="mx-auto max-w-[1200px] px-6 pb-24 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-tech text-[11px] uppercase tracking-[0.4em] text-[#c6a87c]"
        >
          Order Tracking
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.7 }}
          className="mt-6 font-serif-display text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl"
          data-testid="order-status-heading"
        >
          Track your Eurosteel order.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.7 }}
          className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/60 sm:text-lg"
        >
          Enter the email address you used when placing your order to see live production status
          and estimated delivery windows across every project we're building for you.
        </motion.p>

        {/* Search */}
        <motion.form
          onSubmit={search}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18, duration: 0.7 }}
          className="mt-12 flex flex-col gap-3 border-b border-white/15 pb-4 sm:flex-row sm:items-center"
        >
          <input
            data-testid="order-status-input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 bg-transparent font-serif-display text-2xl lowercase tracking-[0.02em] text-white placeholder:text-white/25 focus:outline-none sm:text-3xl"
          />
          <button
            type="submit"
            data-testid="order-status-search-btn"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#c6a87c] px-8 py-3 font-tech text-[11px] uppercase tracking-[0.2em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#d8bd93] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Searching
              </>
            ) : (
              <>
                <Search className="h-4 w-4" /> Track
              </>
            )}
          </button>
        </motion.form>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              key="err"
              data-testid="order-status-error"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="mt-8 flex items-start gap-3 border-l-2 border-red-400/60 bg-red-500/5 p-5 text-red-100/90"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p className="font-body text-sm">{error}</p>
            </motion.div>
          )}

          {ordersList && !order && !detailLoading && (
            <motion.div
              key="list"
              data-testid="order-status-list"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
              className="mt-14"
            >
              <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]">
                {ordersList.length === 1
                  ? "1 order found"
                  : `${ordersList.length} orders found`}
              </p>
              <div className="mt-6 divide-y divide-white/10 border-y border-white/10">
                {ordersList.map((o) => (
                  <button
                    key={o.invoice_number}
                    onClick={() => openOrder(o.invoice_number)}
                    data-testid={`order-list-row-${o.invoice_number}`}
                    className="group flex w-full flex-col items-start gap-4 py-6 text-left transition-colors hover:bg-white/[0.02] sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex-1">
                      <p className="font-serif-display text-xl text-white lg:text-2xl">
                        {o.invoice_number}
                      </p>
                      <p className="mt-2 font-body text-sm text-white/60">
                        {o.product_summary}
                      </p>
                    </div>
                    <div className="flex items-center gap-6 sm:gap-10">
                      <div className="text-left sm:text-right">
                        <p className="font-tech text-[10px] uppercase tracking-[0.28em] text-white/40">
                          Status
                        </p>
                        <p className="mt-1 font-tech text-[11px] font-bold uppercase tracking-[0.2em] text-[#c6a87c]">
                          {o.status}
                        </p>
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="font-tech text-[10px] uppercase tracking-[0.28em] text-white/40">
                          ETA
                        </p>
                        <p className="mt-1 font-body text-sm text-white">
                          {fmtDate(o.eta_date)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {detailLoading && (
            <motion.div
              key="detail-loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-14 flex justify-center py-16"
            >
              <Loader2 className="h-6 w-6 animate-spin text-[#c6a87c]" />
            </motion.div>
          )}

          {order && (
            <motion.div
              key="ok"
              data-testid="order-status-result"
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
              className="mt-14 space-y-14"
            >
              {ordersList && ordersList.length > 1 && (
                <button
                  onClick={backToList}
                  data-testid="order-status-back-btn"
                  className="inline-flex items-center gap-2 font-tech text-[10px] uppercase tracking-[0.28em] text-white/60 transition-colors hover:text-[#c6a87c]"
                >
                  <ChevronLeft size={14} /> Back to all orders
                </button>
              )}

              {/* Summary card */}
              <div className="grid gap-6 border border-white/10 bg-white/[0.02] p-8 lg:grid-cols-3 lg:gap-10 lg:p-10">
                <div>
                  <p className="font-tech text-[10px] uppercase tracking-[0.28em] text-white/40">
                    Invoice
                  </p>
                  <p
                    className="mt-2 font-serif-display text-2xl text-white lg:text-3xl"
                    data-testid="order-invoice-number"
                  >
                    {order.invoice_number}
                  </p>
                </div>
                <div>
                  <p className="font-tech text-[10px] uppercase tracking-[0.28em] text-white/40">
                    Current Status
                  </p>
                  <p
                    className="mt-2 font-serif-display text-2xl text-[#c6a87c] lg:text-3xl"
                    data-testid="order-current-status"
                  >
                    {order.status}
                  </p>
                </div>
                <div>
                  <p className="font-tech text-[10px] uppercase tracking-[0.28em] text-white/40">
                    Estimated Ready
                  </p>
                  <p
                    className="mt-2 font-serif-display text-2xl text-white lg:text-3xl"
                    data-testid="order-eta"
                  >
                    {fmtDate(order.eta_date)}
                  </p>
                </div>
                <div className="lg:col-span-3 lg:border-t lg:border-white/10 lg:pt-6">
                  <p className="font-tech text-[10px] uppercase tracking-[0.28em] text-white/40">
                    Order
                  </p>
                  <p className="mt-2 font-body text-base leading-relaxed text-white/80">
                    {order.product_summary}
                  </p>
                  <p className="mt-4 font-tech text-[10px] uppercase tracking-[0.28em] text-white/30">
                    Last update · {fmtDateTime(order.updated_at)}
                  </p>
                </div>
              </div>

              {/* Progress tracker */}
              <div>
                <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]">
                  Production Progress
                </p>
                <div className="mt-8 hidden md:block">
                  <ProgressBar stages={order.stages} currentIndex={order.stage_index} />
                </div>
                <div className="mt-8 space-y-3 md:hidden">
                  {order.stages.map((s, i) => (
                    <div
                      key={s}
                      className={`flex items-center gap-4 border-l-2 pl-4 py-2 ${
                        i <= order.stage_index ? "border-[#c6a87c]" : "border-white/10"
                      }`}
                    >
                      <span
                        className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] ${
                          i < order.stage_index
                            ? "bg-[#c6a87c] text-[#0a0a0c]"
                            : i === order.stage_index
                            ? "border border-[#c6a87c] text-[#c6a87c]"
                            : "border border-white/20 text-white/40"
                        }`}
                      >
                        {i < order.stage_index ? <Check size={12} /> : i + 1}
                      </span>
                      <span
                        className={`font-body text-sm ${
                          i <= order.stage_index ? "text-white" : "text-white/40"
                        }`}
                      >
                        {s}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeline */}
              {order.timeline?.length > 0 && (
                <div>
                  <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]">
                    Activity
                  </p>
                  <ol className="mt-8 space-y-6" data-testid="order-timeline">
                    {[...order.timeline].reverse().map((ev, i) => (
                      <li
                        key={`${ev.at || "ev"}-${ev.status || ""}-${i}`}
                        className="flex gap-5 border-l border-white/10 pl-6"
                      >
                        <div className="-ml-[30px] flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#c6a87c] bg-[#0a0a0c]">
                          <Package size={10} className="text-[#c6a87c]" />
                        </div>
                        <div>
                          <p className="font-body text-[15px] text-white">{ev.status}</p>
                          <p className="mt-1 font-tech text-[10px] uppercase tracking-[0.24em] text-white/40">
                            {fmtDateTime(ev.at)}
                          </p>
                          {ev.note && (
                            <p className="mt-2 font-body text-sm text-white/60">{ev.note}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {!ordersList && !order && !error && !loading && (
          <div className="mt-16 border border-white/5 bg-white/[0.02] p-8 lg:p-10">
            <p className="font-tech text-[10px] uppercase tracking-[0.28em] text-white/40">
              Which email should I use?
            </p>
            <p className="mt-4 max-w-xl font-body text-sm leading-relaxed text-white/60">
              Use the same email address you gave us when placing your order — it's the one on your
              confirmation from{" "}
              <span className="text-[#c6a87c]">orders@euro-steel.co.nz</span>. Every order tied to
              that email will show up in one place.
            </p>
            <p className="mt-3 max-w-xl font-body text-sm leading-relaxed text-white/60">
              Can't remember? Email{" "}
              <a
                className="text-[#c6a87c] underline-offset-4 hover:underline"
                href="mailto:orders@euro-steel.co.nz"
              >
                orders@euro-steel.co.nz
              </a>{" "}
              with your name and site address and we'll look it up.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

const ProgressBar = ({ stages, currentIndex }) => {
  const pct = stages.length > 1 ? (currentIndex / (stages.length - 1)) * 100 : 0;
  return (
    <div className="relative">
      <div className="absolute left-0 right-0 top-3 h-px bg-white/10" />
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
        className="absolute left-0 top-3 h-px bg-[#c6a87c]"
      />
      <div className="relative grid" style={{ gridTemplateColumns: `repeat(${stages.length}, 1fr)` }}>
        {stages.map((s, i) => {
          const done = i < currentIndex;
          const active = i === currentIndex;
          return (
            <div key={s} className="flex flex-col items-center" data-testid={`stage-${i}`}>
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] transition-colors ${
                  done
                    ? "bg-[#c6a87c] text-[#0a0a0c]"
                    : active
                    ? "border border-[#c6a87c] bg-[#0a0a0c] text-[#c6a87c]"
                    : "border border-white/20 bg-[#0a0a0c] text-white/40"
                }`}
              >
                {done ? <Check size={12} /> : i + 1}
              </span>
              <span
                className={`mt-4 max-w-[120px] text-center font-tech text-[10px] uppercase leading-relaxed tracking-[0.18em] ${
                  done || active ? "text-white" : "text-white/40"
                }`}
              >
                {s}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
